import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();

  const navItems = [
    { path: '/', label: { fr: 'Accueil', en: 'Home' } },
    { path: '/about', label: { fr: 'Le Groupe', en: 'The Group' } },
    { path: '/subsidiaries', label: { fr: 'Nos Filiales', en: 'Our Subsidiaries' } },
    { path: '/synergies', label: { fr: 'Nos Synergies', en: 'Our Synergies' } },
    { path: '/projects', label: { fr: 'Nos Projets', en: 'Our Projects' } },
    { path: '/impact', label: { fr: 'L\'Impact EKIP', en: 'EKIP Impact' } },
    { path: '/news', label: { fr: 'Actualités', en: 'News' } },
    { path: '/contact', label: { fr: 'Contact', en: 'Contact' } },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 transition-opacity hover:opacity-80">
            <div className="overflow-hidden h-20">
              <img 
                src="https://customer-assets.emergentagent.com/job_a6c32c3a-8d7a-49cd-8ea0-9f0781a3c364/artifacts/u3ngpsa5_Logo%20Holding_OS.jpg" 
                alt="EKIP Holding" 
                className="h-28 w-auto object-cover object-top"
                style={{ marginTop: '-4px' }}
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'text-[#003366] bg-[#003366]/5'
                    : 'text-gray-700 hover:text-[#003366] hover:bg-gray-50'
                }`}
              >
                {item.label[language]}
              </Link>
            ))}
          </nav>

          {/* Language Toggle & Contact Info */}
          <div className="hidden lg:flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className="px-3 py-1 text-sm font-semibold text-[#003366] border-2 border-[#003366] rounded-lg hover:bg-[#003366] hover:text-white transition-all duration-200"
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
            <div className="flex flex-col text-right text-xs">
              <a href="tel:+2252722412525" className="flex items-center text-gray-700 hover:text-[#FF6600] transition-colors">
                <Phone size={12} className="mr-1" />
                +225 27 22 41 25 25
              </a>
              <a href="mailto:infoline@ekipholding.ci" className="flex items-center text-gray-700 hover:text-[#FF6600] transition-colors">
                <Mail size={12} className="mr-1" />
                infoline@ekipholding.ci
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-[#003366] transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <nav className="px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  isActive(item.path)
                    ? 'text-[#003366] bg-[#003366]/5'
                    : 'text-gray-700 hover:text-[#003366] hover:bg-gray-50'
                }`}
              >
                {item.label[language]}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-200 space-y-2">
              <button
                onClick={toggleLanguage}
                className="w-full px-4 py-2 text-sm font-semibold text-[#003366] border-2 border-[#003366] rounded-lg hover:bg-[#003366] hover:text-white transition-all"
              >
                {language === 'fr' ? 'English' : 'Français'}
              </button>
              <div className="px-4 py-2 text-sm space-y-1">
                <a href="tel:+2252722412525" className="flex items-center text-gray-700">
                  <Phone size={14} className="mr-2" />
                  +225 27 22 41 25 25 / 05 06 06 55 06
                </a>
                <a href="mailto:infoline@ekipholding.ci" className="flex items-center text-gray-700">
                  <Mail size={14} className="mr-2" />
                  infoline@ekipholding.ci
                </a>
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};